import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Estudiante } from '../../models/estudiante.model';
import { Examen } from '../../models/examen.model';
import { Instructor } from '../../models/instructor.model';
import { Graduacion } from '../../models/graduacion.model';
import { CentroEntrenamiento } from '../../models/centro-entrenamiento.model';
import { EstudianteService } from '../../services/estudiante.service';
import { SharedService } from '../../services/shared.service';
import { GeografiaService } from '../../services/geografia.service';

@Component({
  selector: 'app-estudiante-detail',
  templateUrl: './estudiante-detail.component.html',
  styleUrls: ['./estudiante-detail.component.css']
})
export class EstudianteDetailComponent implements OnInit, OnChanges {
  @Input() estudiante: Estudiante | null = null;
  @Input() initialExamenes: Examen[] | null = null;
  @Input() isNew: boolean = false;
  @Output() cancelEdit = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Estudiante>();

  instructores: Instructor[] = [];
  graduaciones: Graduacion[] = [];
  centros: CentroEntrenamiento[] = [];
  examenes: Examen[] = [];
  
  isEditMode = false;
  isLoading = true;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  // Date limits
  readonly TODAY = new Date().toISOString().split('T')[0];
  readonly MIN_BIRTH_DATE = '1900-01-01';

  constructor(
    private estudianteService: EstudianteService,
    private sharedService: SharedService,
    private geografiaService: GeografiaService
  ) {}

  ngOnInit(): void {
    this.loadSharedData();
    
    if (this.isNew) {
      this.isEditMode = true;
      this.isLoading = false;
    } else if (this.estudiante) {
      this.loadEstudianteData(this.estudiante.nroLibreta);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['estudiante'] && !changes['estudiante'].firstChange) {
      if (this.estudiante && !this.isNew) {
        this.loadEstudianteData(this.estudiante.nroLibreta);
      }
    }
  }

  private loadSharedData(): void {
    this.sharedService.instructores$.subscribe(
      (data) => {
        this.instructores = data;
      },
      (error) => console.error('Error loading instructores:', error)
    );

    this.sharedService.graduaciones$.subscribe(
      (data) => {
        this.graduaciones = data;
      },
      (error) => console.error('Error loading graduaciones:', error)
    );

    this.sharedService.centros$.subscribe(
      (data) => {
        this.centros = this.sortCentros(data);
      },
      (error) => console.error('Error loading centros:', error)
    );
  }

  private loadEstudianteData(nroLibreta: number): void {
    this.isLoading = true;
    this.errorMessage = null;
    
    if (this.initialExamenes && this.initialExamenes.length > 0) {
      this.examenes = this.initialExamenes;
      this.loadEstudianteDetailsOnly(nroLibreta);
    } else {
      this.estudianteService.getEstudianteWithExamenes(nroLibreta).subscribe(
        (data) => {
          this.estudiante = data.estudiante;
          this.examenes = data.examenes;
          this.normalizeAllDates();
          this.isLoading = false;
        },
        (error) => {
          console.error('Error loading estudiante with examenes:', error);
          this.errorMessage = 'Error al cargar los datos del estudiante';
          this.isLoading = false;
        }
      );
    }
  }

  private loadEstudianteDetailsOnly(nroLibreta: number): void {
    this.estudianteService.getEstudiante(nroLibreta).subscribe(
      (data) => {
        this.estudiante = data;
        this.normalizeAllDates();
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading estudiante:', error);
        this.errorMessage = 'Error al cargar los datos del estudiante';
        this.isLoading = false;
      }
    );
  }

  private normalizeAllDates(): void {
    if (this.estudiante) {
      if (this.estudiante.fcNacimiento) {
        this.estudiante.fcNacimiento = this.sharedService.normalizeDate(this.estudiante.fcNacimiento);
      }
      if (this.estudiante.fcInicio) {
        this.estudiante.fcInicio = this.sharedService.normalizeDate(this.estudiante.fcInicio);
      }
      if (this.estudiante.fcUltimoExamen) {
        this.estudiante.fcUltimoExamen = this.sharedService.normalizeDate(this.estudiante.fcUltimoExamen);
      }
    }

    this.examenes.forEach(examen => {
      if (examen.fcExamen) {
        examen.fcExamen = this.sharedService.normalizeDate(examen.fcExamen);
      }
    });
  }

  toggleEditMode(): void {
    if (this.isNew) {
      this.cancel();
      return;
    }
    
    this.isEditMode = !this.isEditMode;
    
    if (!this.isEditMode) {
      this.errorMessage = null;
      this.successMessage = null;
    }
  }

  saveChanges(): void {
    if (!this.estudiante) {
      this.errorMessage = 'No hay datos de estudiante para guardar';
      return;
    }

    // Format dates
    if (this.estudiante.fcNacimiento) {
      this.estudiante.fcNacimiento = this.sharedService.formatDate(this.estudiante.fcNacimiento);
    }
    if (this.estudiante.fcInicio) {
      this.estudiante.fcInicio = this.sharedService.formatDate(this.estudiante.fcInicio);
    }

    if (this.isNew) {
      this.estudianteService.addEstudiante(this.estudiante).subscribe(
        (data) => {
          this.estudiante = data;
          this.successMessage = 'Estudiante registrado correctamente';
          this.errorMessage = null;
          this.isEditMode = false;
          this.isNew = false;
          this.saved.emit(data);
          
          setTimeout(() => {
            this.successMessage = null;
          }, 3000);
        },
        (error) => {
          console.error('Error registering estudiante:', error);
          this.errorMessage = error.error?.error || 'Error al registrar el estudiante';
          this.successMessage = null;
        }
      );
    } else {
      this.estudianteService.updateEstudiante(this.estudiante).subscribe(
        (data) => {
          this.estudiante = data;
          this.successMessage = 'Estudiante actualizado correctamente';
          this.errorMessage = null;
          this.isEditMode = false;
          this.saved.emit(data);
          
          setTimeout(() => {
            this.successMessage = null;
          }, 3000);
        },
        (error) => {
          console.error('Error updating estudiante:', error);
          this.errorMessage = error.error?.error || 'Error al actualizar el estudiante';
          this.successMessage = null;
        }
      );
    }
  }

  cancel(): void {
    this.cancelEdit.emit();
  }

  // Helper methods
  sortCentros(centros: CentroEntrenamiento[]): CentroEntrenamiento[] {
    return centros.sort((a, b) => {
      if (a.activo !== b.activo) {
        return a.activo ? -1 : 1;
      }
      return a.nombre.localeCompare(b.nombre);
    });
  }

  getInstructorNombre(idInstructor?: number): string {
    const id = idInstructor || this.estudiante?.idInstructor;
    if (!id) return 'Sin instructor';
    const instructor = this.instructores.find(i => i.id === id);
    return instructor ? `${instructor.apellido}, ${instructor.nombre}` : 'Sin instructor';
  }

  getCentroNombre(idCentro?: number): string {
    const id = idCentro || this.estudiante?.idCentroEntrenamiento;
    if (!id) return 'Sin centro';
    const centro = this.centros.find(c => c.id === id);
    return centro ? centro.nombre : 'Sin centro';
  }

  getGraduacionNombre(idGraduacion: number | undefined): string {
    if (!idGraduacion) return 'Sin graduación';
    const graduacion = this.graduaciones.find(g => g.id === idGraduacion);
    return graduacion ? graduacion.nombre : 'Sin graduación';
  }

  onInstructorChange(idInstructor: number): void {
    // Add logic here if needed when instructor changes
    console.log('Instructor changed to:', idInstructor);
  }

  onCentroChange(idCentro: number): void {
    // Add logic here if needed when centro changes
    console.log('Centro changed to:', idCentro);
  }

  // Autocomplete filter functions
  filterNacionalidades = (input: string) => {
    return this.geografiaService.filterNacionalidades(input);
  }

  filterLocalidades = (input: string) => {
    return this.geografiaService.filterLocalidades(input);
  }

  filterProvincias = (input: string) => {
    return this.geografiaService.filterProvincias(input);
  }
}